// import axios from 'axios'
import cookies from 'js-cookie'
import * as rax from 'retry-axios'
// import axios from 'axios'
import { setup } from 'axios-cache-adapter'
import store from 'store'

const baseDomain = location.hostname.split('.').slice(-2).join('.')
const apiDomain = import.meta.env.VITE_API_DOMAIN ?? `newsdesk-api.${baseDomain}`
const loginUrl = import.meta.env.VITE_LOGIN_URL ?? `https://${apiDomain}/okta/login`

const api = setup({
  baseURL: `https://${apiDomain}`,
  cache: {
    maxAge: 5 * 1000, // 5 second default
    // debug: true,
    readHeaders: false,
    exclude: {
      query: false
    }
  },
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json;charset=utf-8'
  },
  timeout: 50000
})

// Default axios retry configuration.  Currently retry is set to 0 so
// retries can be scoped to individual calls.  The other defaults
// still apply unless overridden in specific calls.
api.defaults.raxConfig = {
  instance: api,

  // Retry times on requests that return a response (500, etc) before giving up.
  retry: 0,

  // Retry twice on errors that don't return a response (ENOTFOUND, ETIMEDOUT, etc).
  noResponseRetries: 2,

  // Milliseconds to delay at first.  Defaults to 100. Only considered when backoffType is 'static'
  retryDelay: 500,

  backoffType: 'static', // 'exponential' (default), 'static' or 'linear'

  // Response status codes to retry.  Supports a double array with a list of ranges.
  statusCodesToRetry: [
    [400, 429],
    [500, 599]
  ],

  // HTTP methods to automatically retry.
  httpMethodsToRetry: ['GET', 'HEAD', 'OPTIONS', 'DELETE', 'PUT', 'PATCH', 'POST'],

  onRetryAttempt: (err) => {
    const cfg = rax.getConfig(err)
    console.debug(`Retry attempt #${cfg.currentRetryAttempt}`)
  }
}

rax.attach(api)

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (token) {
      prom.resolve(token)
    } else {
      prom.reject(error)
    }
  })
  failedQueue = []
}

// Add a request interceptor to add the Authorization header
api.interceptors.request.use((config) => {
  const accessToken = cookies.get('EWS_AUTH_KEY')
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`
  }
  return config
})

// Add a response interceptor to handle 401 errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    if (401 === error?.response?.status && error.request.responseURL.includes('api.scrippsnet')) {
      // if we got a 401 on the refresh token endpoint, go to login
      if (originalRequest.url === '/v2/auth/refresh_token') {
        console.log('401 error on retry', error)
        store.remove('ews_user')
        store.remove('ews_current_station')
        store.remove('ews_station_settings')
        store.remove('ews_container')
        cookies.remove('EWS_AUTH_KEY')

        const prev = encodeURIComponent(location.href)
        window.location.href = `${loginUrl}?prev=${prev}`
        return Promise.reject(error)
      }
      console.log('401 error', error)
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      try {
        // Call refresh token endpoint
        console.log('refreshing token')
        const response = await api.get('/v2/auth/refresh_token')
        const { jwt } = response.data

        // Update tokens in cookie
        cookies.set('EWS_AUTH_KEY', jwt, { domain: `.${baseDomain}` })

        processQueue(null, jwt)

        // Retry the original request with the new token
        originalRequest.headers['Authorization'] = `Bearer ${jwt}`
        return api(originalRequest)
      } catch (err) {
        processQueue(err, null)
        store.remove('ews_user')
        store.remove('ews_current_station')
        store.remove('ews_station_settings')
        store.remove('ews_container')
        cookies.remove('EWS_AUTH_KEY')

        const prev = encodeURIComponent(location.href)
        window.location.href = `${loginUrl}?prev=${prev}`
        return Promise.reject(err)
      } finally {
        isRefreshing = false
      }
    }

    return Promise.reject(error)
  }
)

export default api
